import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function fmt(d) { return d.toISOString().slice(0, 10); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, d.getDate()); }
function startOfYear(d) { return new Date(d.getFullYear(), 0, 1); }

const PRESETS = [
  { key: "this_month", label: "Цей місяць", range: () => {
    const now = new Date();
    return { from: fmt(startOfMonth(now)), to: fmt(now) };
  }},
  { key: "last_month", label: "Минулий місяць", range: () => {
    const now = new Date();
    const start = startOfMonth(addMonths(now, -1));
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: fmt(start), to: fmt(end) };
  }},
  { key: "last_3m", label: "Останні 3 місяці", range: () => {
    const now = new Date();
    return { from: fmt(addMonths(now, -3)), to: fmt(now) };
  }},
  { key: "last_6m", label: "Останні 6 місяців", range: () => {
    const now = new Date();
    return { from: fmt(addMonths(now, -6)), to: fmt(now) };
  }},
  { key: "ytd", label: "З початку року", range: () => {
    const now = new Date();
    return { from: fmt(startOfYear(now)), to: fmt(now) };
  }},
  { key: "all", label: "Весь час", range: () => ({ from: "", to: "" }) },
];

export default function PeriodFilter({ value, onChange, defaultPreset = "this_month" }) {
  const [preset, setPreset] = useState(defaultPreset);
  const [from, setFrom] = useState(value?.from || "");
  const [to, setTo] = useState(value?.to || "");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (value?.from === undefined && value?.to === undefined) {
      const p = PRESETS.find((p) => p.key === defaultPreset) || PRESETS[0];
      const r = p.range();
      setFrom(r.from); setTo(r.to);
      onChange(r);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (p) => {
    const r = p.range();
    setPreset(p.key); setFrom(r.from); setTo(r.to);
    onChange(r); setOpen(false);
  };
  const applyCustom = () => { setPreset("custom"); onChange({ from, to }); setOpen(false); };

  const activeLabel = PRESETS.find((p) => p.key === preset)?.label || `${from || "—"} … ${to || "—"}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid="period-filter-trigger" className="gap-2">
          <CalendarIcon className="h-4 w-4 text-[#0D9488]" />
          <span className="text-[#0F2027] font-medium">{activeLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3" data-testid="period-filter-content">
        <div className="grid grid-cols-2 gap-1.5 mb-3">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              data-testid={`period-preset-${p.key}`}
              onClick={() => applyPreset(p)}
              className={`text-left text-xs px-3 py-2 rounded-md border transition-colors
                ${preset === p.key ? "border-[#0D9488] bg-teal-50 text-[#0F2027]" : "border-slate-200 hover:border-slate-300"}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="border-t border-slate-200 pt-3 space-y-2">
          <div className="text-xs uppercase tracking-wider text-slate-400">Власний діапазон</div>
          <div className="grid grid-cols-2 gap-2">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="period-from" />
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="period-to" />
          </div>
          <Button onClick={applyCustom} className="w-full bg-[#0F2027] hover:bg-[#0F2027]/90" size="sm" data-testid="period-apply">
            Застосувати
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
